import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    FileText,
    Download,
    Mail,
    Loader2,
    BarChart2,
    PieChart,
    LineChart,
} from "lucide-react";
import { SectionHeader } from "./Services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SampleReport() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!/\S+@\S+\.\S+/.test(email) || loading) return;
        setLoading(true);
        try {
            await axios.post(`${BACKEND_URL}/api/sample-report`, { email });
            setUnlocked(true);
            toast.success("Sample report unlocked", {
                description: "We've sent a copy to your inbox.",
            });
        } catch (err) {
            toast.error("Couldn't unlock", {
                description: "Please try again with a different email.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="sample-report"
            className="relative py-28 bg-[#040A18] border-y border-white/5"
            data-testid="sample-report-section"
        >
            <div className="pr-container">
                <SectionHeader
                    overline="Take a peek"
                    title="Unlock a sample CATI report"
                    subtitle="A 14-page sample of how we deliver findings — methodology, charts, narrative, and the 3 things to act on."
                />

                <div className="mt-14 grid lg:grid-cols-2 gap-12 items-center">
                    {/* Animated PDF mockup */}
                    <div className="relative flex justify-center">
                        <div className="absolute -inset-10 bg-blue-500/20 rounded-[60px] blur-3xl pointer-events-none" />

                        <motion.div
                            initial={{ opacity: 0, y: 30, rotate: -3 }}
                            whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="pdf-mockup w-[260px] sm:w-[300px] rounded-2xl p-5 relative animate-float-slow"
                            data-testid="pdf-mockup-back"
                        >
                            <div className="font-mono-pr text-[10px] text-slate-500 uppercase tracking-widest">
                                Proksy · Vol. 02
                            </div>
                            <div className="mt-3 h-2.5 w-3/5 bg-blue-500/30 rounded" />
                            <div className="mt-2 h-2 w-4/5 bg-white/10 rounded" />
                            <div className="mt-1.5 h-2 w-3/5 bg-white/10 rounded" />
                            <div className="mt-5 grid grid-cols-2 gap-2">
                                <div className="h-12 bg-white/5 rounded border border-white/5 flex items-center justify-center">
                                    <BarChart2 className="w-5 h-5 text-blue-400" />
                                </div>
                                <div className="h-12 bg-white/5 rounded border border-white/5 flex items-center justify-center">
                                    <PieChart className="w-5 h-5 text-emerald-400" />
                                </div>
                            </div>
                            <div className="mt-3 h-1.5 w-full bg-white/5 rounded" />
                            <div className="mt-1 h-1.5 w-5/6 bg-white/5 rounded" />
                            <div className="mt-1 h-1.5 w-4/6 bg-white/5 rounded" />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30, rotate: 3 }}
                            whileInView={{ opacity: 1, y: 0, rotate: 4 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="pdf-mockup w-[260px] sm:w-[300px] rounded-2xl p-5 absolute top-8 sm:top-10 sm:left-1/2 left-1/3 animate-float"
                            data-testid="pdf-mockup-front"
                        >
                            <div className="font-mono-pr text-[10px] text-emerald-300 uppercase tracking-widest">
                                Insights · Q4
                            </div>
                            <div className="mt-3 h-2.5 w-2/3 bg-emerald-400/40 rounded" />
                            <div className="mt-2 h-2 w-5/6 bg-white/10 rounded" />
                            <div className="mt-1.5 h-2 w-2/3 bg-white/10 rounded" />
                            <div className="mt-5 h-24 bg-white/5 rounded border border-white/5 flex items-center justify-center">
                                <LineChart className="w-12 h-12 text-blue-400/70" />
                            </div>
                            <div className="mt-3 h-1.5 w-full bg-white/5 rounded" />
                            <div className="mt-1 h-1.5 w-4/6 bg-white/5 rounded" />
                            <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono-pr text-emerald-300">
                                ▲ +30% recall
                            </div>
                        </motion.div>
                    </div>

                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="pr-glass-strong rounded-3xl p-7 sm:p-8"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-emerald-300" />
                            </div>
                            <div>
                                <p className="font-outfit text-base font-semibold text-white">
                                    Sample CATI Report
                                </p>
                                <p className="text-xs font-mono-pr text-slate-400">
                                    14 pages · 4 charts · executive summary
                                </p>
                            </div>
                        </div>

                        <form
                            onSubmit={submit}
                            className="mt-6 flex flex-col sm:flex-row gap-3"
                            data-testid="sample-report-form"
                        >
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                                    <Mail className="w-4 h-4 text-blue-300" />
                                </span>
                                <input
                                    type="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
                                    data-testid="sample-report-email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary justify-center disabled:opacity-50"
                                data-testid="sample-report-submit"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4" />
                                )}
                                {unlocked ? "Sent" : "Unlock"}
                            </button>
                        </form>
                        <p className="mt-3 text-[11px] text-slate-500 font-mono-pr">
                            We'll email a download link. No spam — single message.
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
